'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import { Button } from '@/components/elements/Button'
import ProductCard from '../product/ProductCard'

const PAGE_SIZE = 5

export default function Products({ products, vendor, title, limit, description, viewAllUrl, eyebrow }: { products: any[], vendor: any, wishlist?: any, title: string, limit?: number, description?: string, viewAllUrl?: string, eyebrow?: string }) {
  const pathname = usePathname() || ''
  const [index, setIndex] = useState(0)
  if (!products || products.length === 0) return null

  const items = limit ? products.slice(0, limit) : products
  const maxIndex = Math.max(0, items.length - PAGE_SIZE)
  const hasNav = items.length > PAGE_SIZE
  const canPrev = index > 0
  const canNext = index < maxIndex

  const slidePct = items.length > 0 ? (100 / items.length) * index : 0
  const trackWidthPct = items.length > 0 ? (items.length / PAGE_SIZE) * 100 : 100

  return (
    <section className="bg-cream py-4 sm:py-8">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            {eyebrow && (
              <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-foreground/55">{eyebrow}</div>
            )}
            <h2
              className="mt-2 font-bold font-gilroy text-foreground leading-[0.96] tracking-[-0.025em]"
              style={{ fontSize: 'clamp(34px,4vw,52px)' }}
            >
              {title}
            </h2>
            {description && (
              <p className="mt-3 text-[15px] text-foreground/70 max-w-xl">{description}</p>
            )}
          </div>
          <div className="hidden lg:flex items-center gap-2">
            {hasNav && (
              <>
                <button
                  onClick={() => setIndex(i => Math.max(0, i - 1))}
                  disabled={!canPrev}
                  aria-label="Өмнөх"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#F1EEE7] text-foreground hover:bg-[#E9E4DA] transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronLeft className="size-4" />
                </button>
                <button
                  onClick={() => setIndex(i => Math.min(maxIndex, i + 1))}
                  disabled={!canNext}
                  aria-label="Дараах"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#F1EEE7] text-foreground hover:bg-[#E9E4DA] transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <ChevronRight className="size-4" />
                </button>
              </>
            )}
            {viewAllUrl && (
              <a href={navigateUrl(viewAllUrl, pathname)} className="ml-2 inline-flex items-center gap-1.5 text-[14px] text-foreground hover:underline group">
                Бүгдийг харах <ArrowRight className="size-3.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
            )}
          </div>
        </div>
        <div className="hidden lg:block overflow-hidden">
          <div
            className="flex gap-5 transition-transform duration-500 ease-out"
            style={{ width: `${trackWidthPct}%`, transform: `translateX(-${slidePct}%)` }}
          >
            {items.map(p => (
              <div key={p.id} className="shrink-0" style={{ width: `calc((100% - ${(items.length - 1) * 1.25}rem) / ${items.length})` }}>
                <ProductCard product={p} vendor={vendor} />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:hidden gap-4">
          {items.map(p => (
            <ProductCard key={p.id} product={p} vendor={vendor} />
          ))}
        </div>
        {viewAllUrl && (
          <div className="lg:hidden mt-8">
            <Button tag="a" href={navigateUrl(viewAllUrl, pathname)} variant="outline" width="full" icon={ArrowRight} iconPosition="right">
              Бүгдийг харах
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}
